import { useEffect, useRef } from 'react'
import { trpc } from '@/config/trpc'
import { useNavigate, useSearchParams } from 'react-router'
import { type AuthState, AUTH_STATE_STORAGE_KEY } from '@/lib/auth/linkedin'
import { toast } from 'sonner'
import { safeParseRnd } from '@/lib/auth/oauth-state'
import { providerErrorReason, trackPlatformConnectFailed } from '@/lib/analytics/connectionTracking'

export function AuthLinkedInPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutateAsync: authorize } = trpc.platform.authorize.useMutation()

  const isMutationRunning = useRef(false)

  // Get params from URL
  const state = searchParams.get('state')
  const code = searchParams.get('code')
  const error = searchParams.get('error')
  const error_description = searchParams.get('error_description')
  const error_reason = searchParams.get('error_reason')

  useEffect(() => {
    if (isMutationRunning.current) {
      console.debug('Mutation already in progress, skipping duplicate call')
      console.debug('This should only happein in development due to StrictMode')
      return
    }

    isMutationRunning.current = true

    // The state comes back through the URL, so it can be missing or garbage.
    const returnedRnd = state ? safeParseRnd(state) : null
    const storedState = JSON.parse(
      localStorage.getItem(AUTH_STATE_STORAGE_KEY) || 'null',
    ) as AuthState | null

    if (!returnedRnd || !storedState || storedState.rnd !== returnedRnd) {
      console.error(`States did not match:\n\tstored: ${storedState}\n\trecievedState: ${state}`)
      // Same recovery as the Facebook page: without it the spinner spins forever.
      isMutationRunning.current = false
      localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
      // Some providers send `?error=…` without echoing the state back. That is still the
      // user cancelling or the provider failing, not a forged callback.
      const reason = !state && error ? providerErrorReason(error, error_reason) : 'state_mismatch'
      trackPlatformConnectFailed('linkedin', reason, error ?? undefined)
      if (reason === 'cancelled') toast('LinkedIn connection cancelled')
      else toast.error('LinkedIn authentication state mismatch. Please try again.')
      navigate('/settings/social-media')
      return
    }

    const onSuccess = () => {
      toast.success('LinkedIn connected successfully')
      navigate(storedState.referrerPath)
    }
    const onError = (errorMessage?: string) => {
      toast.error('Failed to connect LinkedIn', {
        description: errorMessage,
      })
      navigate(storedState.referrerPath)
    }

    new Promise<void>((resolve, reject) => {
      if (code) {
        authorize({ platform: 'linkedin', code })
          .then(() => resolve())
          .catch((e) => {
            trackPlatformConnectFailed('linkedin', 'authorize_failed', e)
            reject(e)
          })
      } else if (error) {
        const reason = providerErrorReason(error, error_reason)
        trackPlatformConnectFailed('linkedin', reason, error)
        reject({
          message: 'Error authenticating with LinkedIn',
          error,
          error_description,
          cancelled: reason === 'cancelled',
        })
      } else {
        trackPlatformConnectFailed('linkedin', 'missing_code')
        reject('BUG: Code and Error not provided')
      }
    })
      .then(() => onSuccess())
      .catch((error) => {
        // Backing out of the consent screen is a choice, not a failure worth a red toast.
        if (error?.cancelled) {
          toast('LinkedIn connection cancelled')
          navigate(storedState.referrerPath)
          return
        }
        console.error(error)
        onError(error?.message || error?.error_description)
      })
      .finally(() => {
        localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
        isMutationRunning.current = false
      })
  }, [state, code, error, error_description, error_reason, authorize, navigate])

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
        <p className="text-muted-foreground">Connecting LinkedIn...</p>
      </div>
    </div>
  )
}
